import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { clientsAPI, entitiesAPI } from '../../services/api';
import { COUNTRY_NAMES } from '../../constants/compliance';
import { useAuth } from '../../contexts/AuthContext';
import { DepartmentManager } from './components/DepartmentManager';
import {
  Building, Plus, Search, Edit, Trash2, MoreHorizontal, ArrowLeft,
  Globe, Mail, Phone, Shield, FileText, Users, AlertTriangle, CheckCircle2, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const ENTITY_TYPES = [
  { value: 'subsidiary', label: 'Subsidiary' },
  { value: 'division', label: 'Division' },
  { value: 'branch', label: 'Branch' },
  { value: 'department', label: 'Department' },
  { value: 'affiliate', label: 'Affiliate' },
];

const JURISDICTIONS = COUNTRY_NAMES;

const MATURITY_TIERS = [
  { value: 'T1', label: 'T1 - Foundational', description: '34 controls for small organizations' },
  { value: 'T2', label: 'T2 - Standard', description: '71 controls for mid-sized organizations' },
  { value: 'T3', label: 'T3 - Advanced', description: '84 controls for large enterprises' },
];

const STATUS_COLORS = {
  active: 'bg-green-500/10 text-green-600 border-green-500/20',
  inactive: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  onboarding: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
};

const ENTITY_REQUIRED_FIELDS = {
  entity_name: 'Entity name is required',
};

// Helper function to convert empty strings to null for all optional fields
const cleanupFormData = (data) => {
  const cleaned = { ...data };
  Object.keys(cleaned).forEach(key => {
    // Convert empty strings to null (except for required fields)
    if (cleaned[key] === '' && !ENTITY_REQUIRED_FIELDS[key]) {
      cleaned[key] = null;
    }
  });
  return cleaned;
};

export default function EntityManagementPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState(null);
  const [entityStats, setEntityStats] = useState({});
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    entity_name: '',
    entity_type: 'subsidiary',
    jurisdiction: '',
    registration_number: '',
    address: '',
    country: '',
    contact_email: '',
    contact_phone: '',
    data_protection_lead: '',
    notes: '',
    status: 'active',
    maturity_tier: '',
  });

  useEffect(() => {
    if (clientId) {
      fetchClientAndEntities();
    } else {
      fetchAllEntities();
    }
  }, [clientId]);

  const fetchClientAndEntities = async () => {
    try {
      const clientResponse = await clientsAPI.getOne(clientId);
      setClient(clientResponse.data);
      setEntities(clientResponse.data.entities || []);
      
      // Fetch stats for each entity
      const statsPromises = (clientResponse.data.entities || []).map(async (entity) => {
        try {
          const statsResponse = await entitiesAPI.getStats(entity.id);
          return { id: entity.id, stats: statsResponse.data };
        } catch {
          return { id: entity.id, stats: {} };
        }
      });
      const statsResults = await Promise.all(statsPromises);
      const statsMap = {};
      statsResults.forEach(r => { statsMap[r.id] = r.stats; });
      setEntityStats(statsMap);
    } catch (error) {
      toast.error('Failed to fetch client data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllEntities = async () => {
    try {
      const response = await entitiesAPI.getAll();
      setEntities(response.data);
    } catch (error) {
      toast.error('Failed to fetch entities');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (fieldsToCheck = Object.keys(ENTITY_REQUIRED_FIELDS)) => {
    const newErrors = {};

    fieldsToCheck.forEach(field => {
      if (!formData[field]?.toString().trim()) {
        newErrors[field] = ENTITY_REQUIRED_FIELDS[field];
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!validateForm()) {
      return;
    }

    try {
      // Clean up empty strings to null for optional fields
      const cleanedData = cleanupFormData({ ...formData, client_id: clientId });

      if (editingEntity) {
        await entitiesAPI.update(editingEntity.id, cleanedData);
        toast.success('Entity updated successfully');
      } else {
        await entitiesAPI.create(cleanedData);
        toast.success('Entity created successfully');
      }
      setDialogOpen(false);
      resetForm();
      setSearchTerm('');  // Clear search after successful save
      if (clientId) {
        fetchClientAndEntities();
      } else {
        fetchAllEntities();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Handle Pydantic validation errors (array of objects) vs simple string errors
      const errorMessage = Array.isArray(detail)
        ? detail.map(e => e.msg || e.message).join(', ')
        : (typeof detail === 'string' ? detail : 'Failed to save entity');
      toast.error(errorMessage);
    }
  };

  const handleDelete = async () => {
    if (!entityToDelete) return;
    try {
      await entitiesAPI.delete(entityToDelete.id);
      toast.success('Entity deleted successfully');
      setDeleteDialogOpen(false);
      setEntityToDelete(null);
      setSearchTerm('');  // Clear search after successful delete
      if (clientId) {
        fetchClientAndEntities();
      } else {
        fetchAllEntities();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entity');
    }
  };

  const resetForm = () => {
    setFormData({
      entity_name: '',
      entity_type: 'subsidiary',
      jurisdiction: '',
      registration_number: '',
      address: '',
      country: '',
      contact_email: '',
      contact_phone: '',
      data_protection_lead: '',
      notes: '',
      status: 'active',
      maturity_tier: '',
    });
    setEditingEntity(null);
  };

  const openEditDialog = (entity) => {
    setEditingEntity(entity);
    setFormData({
      entity_name: entity.entity_name || '',
      entity_type: entity.entity_type || 'subsidiary',
      jurisdiction: entity.country || entity.jurisdiction || '',
      registration_number: entity.registration_number || '',
      address: entity.address || '',
      country: entity.country || entity.jurisdiction || '',
      contact_email: entity.contact_email || '',
      contact_phone: entity.contact_phone || '',
      data_protection_lead: entity.data_protection_lead || '',
      notes: entity.notes || '',
      status: entity.status || 'active',
      maturity_tier: entity.maturity_tier || '',
    });
    setDialogOpen(true);
  };

  const filteredEntities = entities.filter(entity => {
    const matchesSearch = entity.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entity.entity_code?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const canEdit = user?.role === 'dpo' || user?.role === 'client_admin';

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="entity-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {clientId && (
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin/clients')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <div>
              <h1 className="text-2xl font-bold">
                {client ? `${client.client_name} - Entities` : 'Entity Management'}
              </h1>
              <p className="text-muted-foreground">
                {client ? `Manage entities for ${client.client_code}` : 'View and manage all entities'}
              </p>
            </div>
          </div>
          {canEdit && clientId && client?.client_type === 'group' && (
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-entity-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Entity
            </Button>
          )}
        </div>

        {/* Client Info Card (if viewing specific client) */}
        {client && (
          <>
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <span>{client.country || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{client.contact_email || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{client.contact_phone || 'N/A'}</span>
                  </div>
                  <Badge className={STATUS_COLORS[client.status]}>{client.status}</Badge>
                </div>
              </CardContent>
            </Card>
            {client.client_type !== 'group' && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardContent className="p-4">
                  <p className="text-sm text-blue-900">
                    <span className="font-medium">ℹ️ Auto-Managed Entity:</span> This {client.client_type} client has an automatically created primary entity. You can view and edit it below, but additional entities cannot be created.
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search entities..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-entities"
              />
            </div>
          </CardContent>
        </Card>

        {/* Entities Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entity</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Jurisdiction</TableHead>
                  {!clientId && <TableHead>Client</TableHead>}
                  <TableHead>Compliance Stats</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={clientId ? 6 : 7} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredEntities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={clientId ? 6 : 7} className="text-center py-8">
                      <Building className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">No entities found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEntities.map((entity) => {
                    const stats = entityStats[entity.id] || {};
                    return (
                      <TableRow
                        key={entity.id}
                        data-testid={`entity-row-${entity.id}`}
                        onClick={() => setSelectedEntity(entity)}
                        className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/10">
                              <Building className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium">{entity.entity_name}</p>
                                {entity.is_primary && (
                                  <Badge variant="outline" className="text-xs">Primary</Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground font-mono">{entity.entity_code}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {ENTITY_TYPES.find(t => t.value === entity.entity_type)?.label || entity.entity_type || 'N/A'}
                          </Badge>
                        </TableCell>
                        <TableCell>{entity.jurisdiction || entity.country || '-'}</TableCell>
                        {!clientId && (
                          <TableCell>
                            <span className="text-sm">{entity.client_name}</span>
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-2 text-xs">
                            <div className="flex items-center gap-1" title="ROPA Records">
                              <FileText className="h-3 w-3" />
                              <span>{stats.ropa_count || 0}</span>
                            </div>
                            <div className="flex items-center gap-1" title="Breaches">
                              <AlertTriangle className="h-3 w-3" />
                              <span>{stats.breach_count || 0}</span>
                            </div>
                            <div className="flex items-center gap-1" title="DSRs">
                              <Users className="h-3 w-3" />
                              <span>{stats.dsr_count || 0}</span>
                            </div>
                            <div className="flex items-center gap-1" title="Vendors">
                              <Shield className="h-3 w-3" />
                              <span>{stats.vendor_count || 0}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[entity.status] || STATUS_COLORS.active}>
                            {entity.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {canEdit && !entity.is_primary && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`entity-menu-${entity.id}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditDialog(entity)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => { setEntityToDelete(entity); setDeleteDialogOpen(true); }}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Department Manager Section */}
        {selectedEntity && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Departments for {selectedEntity.entity_name}</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedEntity(null)}
                >
                  ✕ Close
                </Button>
              </div>
              <DepartmentManager
                entityId={selectedEntity.id}
                entityName={selectedEntity.entity_name}
              />
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEntity ? 'Edit Entity' : 'Add New Entity'}</DialogTitle>
              <DialogDescription>
                {editingEntity ? 'Update entity information' : 'Create a new entity (subsidiary/division/branch)'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="entity_name">Entity Name *</Label>
                  <Input
                    id="entity_name"
                    value={formData.entity_name}
                    onChange={(e) => {
                      setFormData({ ...formData, entity_name: e.target.value });
                      // Clear error when user starts typing
                      if (errors.entity_name) setErrors({ ...errors, entity_name: null });
                    }}
                    placeholder="e.g., JK Enterprises"
                    required
                    data-testid="entity-name-input"
                    className={errors.entity_name ? 'border-destructive' : ''}
                  />
                  {errors.entity_name && (
                    <p className="text-xs text-destructive flex items-center gap-1 mt-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.entity_name}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="entity_type">Entity Type</Label>
                  <Select value={formData.entity_type} onValueChange={(v) => setFormData({ ...formData, entity_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ENTITY_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="onboarding">Onboarding</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="jurisdiction">Jurisdiction</Label>
                  <Select value={formData.country || formData.jurisdiction} onValueChange={(v) => setFormData({ ...formData, country: v, jurisdiction: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select jurisdiction" />
                    </SelectTrigger>
                    <SelectContent>
                      {JURISDICTIONS.map(j => (
                        <SelectItem key={j} value={j}>{j}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="maturity_tier">Maturity Assessment Tier (Optional)</Label>
                  <Select value={formData.maturity_tier || 'inherit'} onValueChange={(v) => setFormData({ ...formData, maturity_tier: v === 'inherit' ? '' : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Inherit from client" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inherit">Inherit from client</SelectItem>
                      {MATURITY_TIERS.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          <div>
                            <p className="font-medium">{t.label}</p>
                            <p className="text-xs text-muted-foreground">{t.description}</p>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Override the client's maturity tier for this entity's assessments</p>
                </div>
                <div>
                  <Label htmlFor="registration_number">Registration Number</Label>
                  <Input
                    id="registration_number"
                    value={formData.registration_number}
                    onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                    placeholder="Company registration #"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Full address"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="contact@entity.com"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="+1 (876) 555-0123"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="data_protection_lead">Data Protection Lead</Label>
                  <Input
                    id="data_protection_lead"
                    value={formData.data_protection_lead}
                    onChange={(e) => setFormData({ ...formData, data_protection_lead: e.target.value })}
                    placeholder="Name of internal DP contact"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes"
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-entity-btn">
                  {editingEntity ? 'Update Entity' : 'Create Entity'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Entity</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{entityToDelete?.entity_name}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-entity-btn">
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
